<script lang="ts">
    import SpriteIcon from '$m7-library/component/Icons/SpriteIcon.svelte';
    import type {VariantData, VariantSelection} from './types';

    let {
        variantData,
        selectedVariants,
        openDropdown = $bindable<string | null>(),
        onSelect,
    }: {
        variantData: VariantData;
        selectedVariants: Record<string, string>;
        openDropdown: string | null;
        onSelect: (inputName: string, selectionId: string) => void;
    } = $props();

    let dropdownPosition = $state({top: 0, left: 0, width: 0});

    function getSelectedOptionTitle(selection: VariantSelection) {
        const selectedId = selectedVariants[selection.inputName];
        if (!selectedId) return selection.name;
        const option = selection.options.find(opt => opt.selectionId === selectedId);
        return option ? `${selection.name}: ${option.title}` : selection.name;
    }

    function toggleDropdown(inputName: string, event: MouseEvent) {
        if (openDropdown === inputName) {
            openDropdown = null;
        } else {
            const button = event.currentTarget as HTMLElement;
            const rect = button.getBoundingClientRect();
            dropdownPosition = {
                top: rect.bottom,
                left: rect.left,
                width: rect.width,
            };
            openDropdown = inputName;
        }
    }

    function closeDropdown() {
        openDropdown = null;
    }
</script>

<svelte:window onscroll={closeDropdown} onresize={closeDropdown} onclick={closeDropdown}/>

<div class="tw:flex tw:flex-col tw:items-start tw:gap-2.5 tw:w-full"
     role="none"
     onclick={(e) => e.stopPropagation()}
     onkeydown={(e) => e.stopPropagation()}>
    {#if variantData}
        {#each variantData.selections as selection, i}
            <div class="tw:w-full">
                {#if selection.label === 'color'}
                    <div class="tw:flex tw:flex-col tw:justify-center tw:w-full tw:mb-2">
                        <span class="tw:text-cyprus tw:text-xs tw:leading-[18px]">{selection.name}{#each selection.options as option}{#if selectedVariants[selection.inputName] === option.selectionId}: <span class="tw:font-bold">{option.title}</span>{/if}{/each}</span>
                    </div>
                    <div class="tw:flex tw:flex-wrap tw:gap-2 tw:w-full">
                        {#each selection.options as option}
                            <button type="button"
                                    onclick={() => onSelect(selection.inputName, option.selectionId)}
                                    class="tw:flex tw:justify-center tw:items-center tw:p-1.5 tw:bg-white tw:cursor-pointer tw:border tw:transition-all tw:duration-200
                                    {selectedVariants[selection.inputName] === option.selectionId ? 'tw:border-elm tw:border-b-[3px] tw:rounded-md' : 'tw:border-line tw:rounded-md tw:hover:border-cyprus/40'}
                                    {option.stockInfo === 0 ? 'tw:opacity-50' : ''}"
                                    style="width:70px; height: 55px" title={option.title}>
                                <picture class="tw:flex tw:justify-center tw:items-center tw:w-full tw:h-full">
                                    {#if option.sources}
                                        <source type="image/webp" srcset={option.sources.srcset}>
                                    {/if}
                                    {#if option.img}
                                        <img src={option.img.src} alt={option.img.alt}
                                             class="tw:object-contain tw:max-w-full tw:max-h-full">
                                    {/if}
                                </picture>
                            </button>
                        {/each}
                    </div>
                {:else}
                    <div class="tw:relative tw:w-full tw:mt-2">
                        <button type="button" id={`variant-dropdown-${i}`}
                                class="tw:flex tw:items-center tw:justify-between tw:w-full tw:px-4 tw:py-2 tw:bg-white tw:border tw:border-line tw:rounded-md tw:outline-none tw:focus:border-cyprus tw:cursor-pointer"
                                onclick={(e) => toggleDropdown(selection.inputName, e)}>
                            <span class="tw:flex tw:items-center tw:gap-2">
                                {#if selection.label === 'size'}
                                    <SpriteIcon name="size" class="tw:size-4 tw:text-gray-400"/>
                                {/if}
                                <span class="tw:text-dark tw:text-base tw:font-normal tw:leading-6 tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap">
                                    {getSelectedOptionTitle(selection)}
                                </span>
                            </span>
                            <SpriteIcon name="down" class="tw:size-4"></SpriteIcon>
                        </button>

                        {#if openDropdown === selection.inputName}
                            <div class="tw:fixed tw:z-[9999] tw:bg-white tw:shadow-lg tw:border tw:border-line tw:max-h-[300px] tw:overflow-y-auto tw:rounded-b-md"
                                 style="top: {dropdownPosition.top}px; left: {dropdownPosition.left}px; width: {dropdownPosition.width}px;"
                                 role="none"
                                 onclick={(e) => e.stopPropagation()}
                                 onkeydown={(e) => e.stopPropagation()}>
                                {#each selection.options as option}
                                    <button type="button"
                                            onclick={() => onSelect(selection.inputName, option.selectionId)}
                                            class="tw:flex tw:items-center tw:hover:bg-gray-50 tw:py-2 tw:px-3 tw:w-full tw:text-left tw:text-sm tw:cursor-pointer tw:text-dark">
                                        {#if option.stockColor}
                                            <span class="tw:inline-block tw:mr-2 {option.stockColor}"
                                                  style="width: 10px; height: 10px; border-radius: 50%"></span>
                                        {:else}
                                            <span class="tw:inline-block tw:mr-2"
                                                  style="width: 10px; height: 10px;"></span>
                                        {/if}
                                        {option.title}
                                    </button>
                                {/each}
                            </div>
                        {/if}
                    </div>
                {/if}
            </div>
        {/each}
    {/if}
</div>
